import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useMenuIcon, useMenuName } from '@/hooks/useMenuIcon';
import { PageContainer, PageHeader } from '@/components/layout';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Activity,
  AlertCircle,
  RefreshCw,
  Server,
  Cpu,
  Database,
  Layers,
  Clock,
  CheckCircle2,
  XCircle,
  HardDrive,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { API_BASE_URL } from '@/api/apiConfig';
import { Spinner } from '@/components/ui/spinner';

const HEALTH_URL = `${API_BASE_URL}/api/health/detail`;

interface JvmInfo {
  freeMemory: string;
  totalMemory: string;
  maxMemory: string;
  processors: number;
}

interface EnvInfo {
  activeProfiles: string[];
  serverPort: string;
}

interface HealthDetail {
  status: string;
  timestamp: string;
  service: string;
  jvm: JvmInfo;
  environment: EnvInfo;
}

const parseMemoryMB = (value: string): number => {
  return parseInt(value.replace(' MB', ''), 10) || 0;
};

const MemoryBar: React.FC<{ used: number; total: number; max: number }> = ({ used, total, max }) => {
  const usedPct = max > 0 ? Math.round((used / max) * 100) : 0;
  const allocatedPct = max > 0 ? Math.round((total / max) * 100) : 0;
  const color =
    usedPct > 85 ? 'bg-red-500' : usedPct > 65 ? 'bg-yellow-500' : 'bg-blue-500';

  return (
    <div className="w-full">
      <div className="flex justify-between text-xs text-muted-foreground mb-1">
        <span>사용 중: {used} MB</span>
        <span>최대: {max} MB</span>
      </div>
      <div className="relative h-3 rounded-full bg-muted overflow-hidden">
        {/* allocated bar */}
        <div
          className="absolute h-full rounded-full bg-blue-200"
          style={{ width: `${allocatedPct}%` }}
        />
        {/* used bar */}
        <div
          className={cn('absolute h-full rounded-full transition-all duration-500', color)}
          style={{ width: `${usedPct}%` }}
        />
      </div>
      <div className="mt-1 text-xs text-right text-muted-foreground">{usedPct}% 사용</div>
    </div>
  );
};

export default function SystemHealth() {
  const menuIcon = useMenuIcon();
  const menuName = useMenuName();
  const [health, setHealth] = useState<HealthDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token =
        localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const res = await axios.get<HealthDetail>(HEALTH_URL, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setHealth(res.data);
      setLastFetched(new Date());
    } catch (err: any) {
      const msg =
        err.response?.data?.message ||
        err.response?.data ||
        '헬스 체크 정보를 불러오지 못했습니다.';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchHealth, 15000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchHealth]);

  const isUp = health?.status === 'UP';
  const usedMemory = health
    ? parseMemoryMB(health.jvm.totalMemory) - parseMemoryMB(health.jvm.freeMemory)
    : 0;
  const totalMemory = health ? parseMemoryMB(health.jvm.totalMemory) : 0;
  const maxMemory = health ? parseMemoryMB(health.jvm.maxMemory) : 0;

  const formatTimestamp = (ts?: string) => {
    if (!ts) return '-';
    try {
      return new Date(ts).toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return ts;
    }
  };

  return (
    <PageContainer maxWidth="full">
      <PageHeader 
        title={menuName || '시스템 헬스 체크'}
        description="백엔드 서버의 JVM 메모리 및 환경 상태를 실시간으로 확인합니다."
        icon={menuIcon || '🖥️'}
      />

      <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAutoRefresh((v) => !v)}
            className={cn(autoRefresh && 'border-blue-500 text-blue-600')}
          >
            <Activity className="w-4 h-4 mr-1" />
            {autoRefresh ? '자동 새로고침 중 (15s)' : '자동 새로고침'}
          </Button>
          <Button
            size="sm"
            onClick={fetchHealth}
            disabled={loading}
          >
            {loading ? (
              <Spinner size="sm" className="mr-1" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-1" />
            )}
            새로고침
          </Button>
        </div>

      {/* 에러 */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* 로딩 스켈레톤 */}
      {loading && !health && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-28 rounded-xl bg-muted animate-pulse" />
          ))}
        </div>
      )}

      {health && (
        <>
          {/* 상단 요약 카드 3개 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* 서비스 상태 */}
            <Card className={cn('border-l-4', isUp ? 'border-l-green-500' : 'border-l-red-500')}>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isUp ? (
                      <CheckCircle2 className="w-8 h-8 text-green-500" />
                    ) : (
                      <XCircle className="w-8 h-8 text-red-500" />
                    )}
                    <div>
                      <p className="text-xs text-muted-foreground">서비스 상태</p>
                      <p className="text-xl font-bold">{health.service}</p>
                    </div>
                  </div>
                  <Badge
                    className={cn(
                      'text-sm px-3 py-1',
                      isUp
                        ? 'bg-green-100 text-green-800 border-green-200'
                        : 'bg-red-100 text-red-800 border-red-200'
                    )}
                  >
                    {health.status}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* CPU */}
            <Card className="border-l-4 border-l-purple-400">
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center gap-3">
                  <Cpu className="w-8 h-8 text-purple-400" />
                  <div>
                    <p className="text-xs text-muted-foreground">가용 프로세서</p>
                    <p className="text-3xl font-bold">{health.jvm.processors}</p>
                    <p className="text-xs text-muted-foreground">logical cores</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 서버 포트 */}
            <Card className="border-l-4 border-l-orange-400">
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center gap-3">
                  <Server className="w-8 h-8 text-orange-400" />
                  <div>
                    <p className="text-xs text-muted-foreground">서버 포트</p>
                    <p className="text-3xl font-bold">{health.environment.serverPort}</p>
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {health.environment.activeProfiles.length > 0 ? (
                        health.environment.activeProfiles.map((p) => (
                          <Badge key={p} className="text-xs bg-orange-100 text-orange-700 border-orange-200">
                            {p}
                          </Badge>
                        ))
                      ) : (
                        <Badge className="text-xs bg-gray-100 text-gray-600">default</Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* JVM 메모리 상세 */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Database className="w-5 h-5 text-blue-500" />
                JVM 메모리
              </CardTitle>
              <CardDescription>힙 메모리 할당 및 사용 현황</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* 메모리 바 */}
              <MemoryBar used={usedMemory} total={totalMemory} max={maxMemory} />

              {/* 메모리 수치 그리드 */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  {
                    label: '사용 중 (Used)',
                    value: `${usedMemory} MB`,
                    icon: <HardDrive className="w-4 h-4 text-blue-500" />,
                    color: 'text-blue-600',
                  },
                  {
                    label: '여유 (Free)',
                    value: health.jvm.freeMemory,
                    icon: <HardDrive className="w-4 h-4 text-green-500" />,
                    color: 'text-green-600',
                  },
                  {
                    label: '할당 (Total)',
                    value: health.jvm.totalMemory,
                    icon: <HardDrive className="w-4 h-4 text-yellow-500" />,
                    color: 'text-yellow-600',
                  },
                  {
                    label: '최대 (Max)',
                    value: health.jvm.maxMemory,
                    icon: <HardDrive className="w-4 h-4 text-purple-500" />,
                    color: 'text-purple-600',
                  },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex flex-col gap-1 rounded-lg border bg-muted/30 p-3"
                  >
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      {item.icon}
                      {item.label}
                    </div>
                    <p className={cn('text-lg font-bold', item.color)}>{item.value}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* 환경 / 타임스탬프 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 환경 정보 */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Layers className="w-5 h-5 text-indigo-500" />
                  환경 정보
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between rounded-lg border bg-muted/30 px-4 py-3">
                  <span className="text-sm text-muted-foreground">서버 포트</span>
                  <span className="font-semibold">{health.environment.serverPort}</span>
                </div>
                <div className="flex items-start justify-between rounded-lg border bg-muted/30 px-4 py-3">
                  <span className="text-sm text-muted-foreground">활성 프로파일</span>
                  <div className="flex flex-wrap justify-end gap-1 max-w-[60%]">
                    {health.environment.activeProfiles.length > 0 ? (
                      health.environment.activeProfiles.map((p) => (
                        <Badge key={p} className="bg-indigo-100 text-indigo-700 border-indigo-200 text-xs">
                          {p}
                        </Badge>
                      ))
                    ) : (
                      <Badge className="bg-gray-100 text-gray-600 text-xs">default</Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between rounded-lg border bg-muted/30 px-4 py-3">
                  <span className="text-sm text-muted-foreground">서비스명</span>
                  <span className="font-semibold font-mono text-sm">{health.service}</span>
                </div>
              </CardContent>
            </Card>

            {/* 타임스탬프 */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Clock className="w-5 h-5 text-teal-500" />
                  시간 정보
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between rounded-lg border bg-muted/30 px-4 py-3">
                  <span className="text-sm text-muted-foreground">서버 응답 시각</span>
                  <span className="font-semibold text-sm">{formatTimestamp(health.timestamp)}</span>
                </div>
                <div className="flex items-center justify-between rounded-lg border bg-muted/30 px-4 py-3">
                  <span className="text-sm text-muted-foreground">마지막 조회</span>
                  <span className="font-semibold text-sm">
                    {lastFetched ? formatTimestamp(lastFetched.toISOString()) : '-'}
                  </span>
                </div>
                <div className="flex items-center justify-between rounded-lg border bg-muted/30 px-4 py-3">
                  <span className="text-sm text-muted-foreground">자동 새로고침</span>
                  <Badge
                    className={cn(
                      'text-xs',
                      autoRefresh
                        ? 'bg-blue-100 text-blue-700 border-blue-200'
                        : 'bg-gray-100 text-gray-600'
                    )}
                  >
                    {autoRefresh ? '15초 간격 활성' : '비활성'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </PageContainer>
  );
}
